import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { appeler } from '../../api/client.ts';
import { Chargement, Erreur } from '../../composants/communs.tsx';
import { Entete } from './detail-entete.tsx';
import { InfoBarre } from './detail-info.tsx';
import { Articles } from './detail-articles.tsx';
import { Reglement } from './detail-reglement.tsx';
import { BlocPhotos } from './detail-photos.tsx';
import { Historique } from './detail-historique.tsx';
import type { DetailVente } from './types.ts';

/**
 * Détail d'une vente — RG-004, RG-046, RG-143.
 *
 * La liste ne montre qu'un total. Comprendre une vente contestée exige de voir
 * ce qui la compose : les lignes, les remises, l'arrondi, les règlements.
 * Sans cet écran, un litige au comptoir se règle de mémoire.
 *
 * Mise en page du 04/10/2026 : en-tête avec référence copiable et statut,
 * bandeau d'infos en icônes, articles + marge à gauche, règlement et photo
 * dans un panneau à droite, historique en dessous.
 */
export function DetailVenteVue({ venteId }: { venteId: string }): ReactNode {
  const { data, isPending, error } = useQuery({
    queryKey: ['vente', venteId],
    queryFn: () => appeler<DetailVente>(`/ventes/${venteId}`),
  });

  if (isPending) return <Chargement />;
  // Pas de garde sur `data` : après `isPending` faux et `error` nul, la
  // requête a nécessairement abouti. Le type le dit, et la garde faisait
  // croire à un cas qui ne peut pas se produire.
  if (error !== null) return <Erreur erreur={error} />;

  return (
    <div className="pile">
      <Entete vente={data} />
      <InfoBarre vente={data} />

      <div className="fiche-mise-en-page">
        <div className="pile">
          <Articles vente={data} />
        </div>

        <div className="pile">
          <Reglement vente={data} />
          <BlocPhotos venteId={venteId} />
          <Historique vente={data} />
        </div>
      </div>
    </div>
  );
}
